import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';

type Mode = 'light' | 'dark' | 'system';
type Ctx = { mode: Mode; effective: 'light' | 'dark'; setMode: (m: Mode) => void; toggle: () => void };

const C = createContext<Ctx | null>(null);

function resolve(mode: Mode): 'light' | 'dark' {
  if (mode === 'system') return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  return mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeRaw] = useState<Mode>(() => {
    const saved = (localStorage.getItem('theme') as Mode | null);
    return saved ?? 'system';
  });
  const [effective, setEffective] = useState<'light'|'dark'>(() => resolve(mode));

  useEffect(() => {
    const apply = () => {
      const eff = resolve(mode);
      setEffective(eff);
      document.documentElement.classList.toggle('dark', eff === 'dark');
    };
    apply();
    if (mode === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.addEventListener('change', apply);
      return () => mql.removeEventListener('change', apply);
    }
  }, [mode]);

  const setMode = useCallback((m: Mode) => {
    setModeRaw(m);
    if (m === 'system') localStorage.removeItem('theme'); else localStorage.setItem('theme', m);
  }, []);
  const toggle = useCallback(() => setMode(effective === 'dark' ? 'light' : 'dark'), [effective, setMode]);

  return <C.Provider value={{ mode, effective, setMode, toggle }}>{children}</C.Provider>;
}

export function useTheme() {
  const v = useContext(C);
  if (!v) throw new Error('useTheme outside provider');
  return v;
}
